export type ZoomEventType = 'MEETING' | 'WEBINAR' | 'EVENT'
export type PendingEventRegistration = {
  version: 1
  intentId: string
  eventSlug: string
  secureEventPath: string
  zoomEventId: string
  zoomEventType: ZoomEventType
  ticketTypeId?: string
  createdAt: number
  expiresAt: number
}

export const EVENT_INTENT_LIFETIME = 60 * 60 * 1000

const normalizeIdentifier = (value: unknown, pattern: RegExp): string | undefined => {
  if (typeof value !== 'string' && typeof value !== 'number') return undefined
  if (typeof value === 'number' && (!Number.isSafeInteger(value) || value < 0)) return undefined
  const id = String(value)
  return id.length <= 256 && pattern.test(id) ? id : undefined
}

export const normalizeEventIdentifier = (value: unknown): string | undefined =>
  normalizeIdentifier(value, /^[A-Za-z0-9_-]+$/)

// CMS Zoom IDs may contain internal ASCII spaces. Keep that exact identity in both apps;
// ticket IDs continue using the strict validator above.
export const normalizeZoomEventIdentifier = (value: unknown): string | undefined =>
  normalizeIdentifier(value, /^[A-Za-z0-9_-]+(?: +[A-Za-z0-9_-]+)*$/)

export const isZoomEventType = (value: unknown): value is ZoomEventType =>
  value === 'MEETING' || value === 'WEBINAR' || value === 'EVENT'

/** Accept only local event detail routes; query/hash never determine event identity. */
export const eventSlugFromPath = (path: string): string | undefined =>
  /^(?:\/secure\/index)?\/essentials\/events\/([A-Za-z0-9_-]+)\/?(?:[?#].*)?$/.exec(path)?.[1]

export const secureEventPath = (slug: string) => `/secure/index/essentials/events/${slug}`

/** Validate a saved selection before using it for redirects or automatic registration. */
export const isValidPendingEventRegistration = (
  value: unknown,
  now = Date.now(),
): value is PendingEventRegistration => {
  if (typeof value !== 'object' || value === null) return false
  const record = value as PendingEventRegistration
  if (record.version !== 1) return false

  // Ensure the saved destination points to the same event.
  const hasValidEventReference =
    typeof record.intentId === 'string' &&
    /^[A-Za-z0-9_-]{1,128}$/.test(record.intentId) &&
    typeof record.eventSlug === 'string' &&
    /^[A-Za-z0-9_-]+$/.test(record.eventSlug) &&
    record.secureEventPath === secureEventPath(record.eventSlug)

  // Zoom Events require a ticket; meetings and webinars may omit it.
  const hasValidTicket =
    record.ticketTypeId === undefined
      ? record.zoomEventType !== 'EVENT'
      : typeof record.ticketTypeId === 'string' && normalizeEventIdentifier(record.ticketTypeId) === record.ticketTypeId

  const hasValidSessionSelection =
    typeof record.zoomEventId === 'string' &&
    normalizeZoomEventIdentifier(record.zoomEventId) === record.zoomEventId &&
    isZoomEventType(record.zoomEventType) &&
    hasValidTicket

  // Only replay selections within one hour of when they were saved.
  const hasValidExpiry =
    Number.isFinite(record.createdAt) &&
    record.createdAt <= now &&
    record.expiresAt === record.createdAt + EVENT_INTENT_LIFETIME &&
    record.expiresAt > now

  return hasValidEventReference && hasValidSessionSelection && hasValidExpiry
}
